// "Unshield to origin" — recover funds from a POI-BLOCKED shield (RAILGUN
// `ShieldBlocked` bucket). The shield landed on-chain but the POI aggregator
// rejected the commitment, so the notes are not spendable in private mode and a
// broadcaster will not serve them. This flow withdraws them back to the ORIGINAL
// public address that did the shield — recipient is derived by the SDK from the
// shield txid, the user can't choose another. Self-signed + self-paid gas only
// (no broadcaster), which is exactly what the to-origin SDK enforces: its
// signatures have NO broadcaster / feeToken / sendWithPublicWallet params.
//
// Mirrors the routing of `railgunUnshield.ts`: engine in-process (flag off) vs
// offscreen (flag on, webkit). On mobile-web these `*WithSDK` functions run
// inside the nested railgun worker (seed never leaves it); see railgun.worker.ts.
import type {
  getERC20AndNFTAmountRecipientsForUnshieldToOrigin,
  gasEstimateForUnprovenUnshieldToOrigin,
  generateUnshieldToOriginProof,
  populateProvedUnshieldToOrigin
} from '@railgun-community/wallet'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import {
  TXIDVersion,
  type RailgunERC20AmountRecipient,
  type RailgunNFTAmountRecipient,
  type TransactionGasDetails
} from '@railgun-community/shared-models'
import { JsonRpcProvider } from 'ethers'

import { getNetworkNameForChainId, getRpcUrlForChainId, RAILGUN_DEBUG } from './railgunCore'
import { derivePublicSigner } from './railgunMnemonic'
import {
  getErrorMessage,
  getGasDetailsForTransaction,
  computeGasFeeNativeAndUsd,
  computeGasFeeOptionsNativeAndUsd,
  warmMainEngineForChain,
  type RailgunGasSpeedName
} from './railgunShared'
import { FEATURES } from '@web/constants/features'
import { sdkInvokeInOffscreen } from '@web/extension-services/background/offscreen/railgunOffscreenClient'
import type { UnshieldGasEstimation } from './railgunUnshield'

function wrapUnshieldToOriginError(err: unknown): never {
  const message = getErrorMessage('unshield', err)
  throw err instanceof Error && message === err.message ? err : new Error(message, { cause: err })
}

// --- offscreen-vs-in-process routing (same shape as railgunUnshield.ts) ---

async function invokeGetRecipientsForUnshieldToOrigin(
  args: Parameters<typeof getERC20AndNFTAmountRecipientsForUnshieldToOrigin>
): Promise<Awaited<ReturnType<typeof getERC20AndNFTAmountRecipientsForUnshieldToOrigin>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({
      method: 'getERC20AndNFTAmountRecipientsForUnshieldToOrigin',
      args
    })
  }
  const { getERC20AndNFTAmountRecipientsForUnshieldToOrigin } = await loadRailgunSdk()
  return getERC20AndNFTAmountRecipientsForUnshieldToOrigin(...args)
}

async function invokeGasEstimateForUnshieldToOrigin(
  args: Parameters<typeof gasEstimateForUnprovenUnshieldToOrigin>
): Promise<Awaited<ReturnType<typeof gasEstimateForUnprovenUnshieldToOrigin>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'gasEstimateForUnprovenUnshieldToOrigin', args })
  }
  const { gasEstimateForUnprovenUnshieldToOrigin } = await loadRailgunSdk()
  return gasEstimateForUnprovenUnshieldToOrigin(...args)
}

async function invokeGenerateUnshieldToOriginProof(
  args: Parameters<typeof generateUnshieldToOriginProof>,
  onProgress?: (progress: number) => void
): Promise<void> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    // The progress callback can't survive structured-clone; replace it with null
    // on the wire and let the offscreen registry splice a real one from progressId.
    const wireArgs: unknown[] = [...args]
    wireArgs[wireArgs.length - 1] = null
    await sdkInvokeInOffscreen({
      method: 'generateUnshieldToOriginProof',
      args: wireArgs,
      onProgress
    })
    return
  }
  const { generateUnshieldToOriginProof } = await loadRailgunSdk()
  await generateUnshieldToOriginProof(...args)
}

async function invokePopulateProvedUnshieldToOrigin(
  args: Parameters<typeof populateProvedUnshieldToOrigin>
): Promise<Awaited<ReturnType<typeof populateProvedUnshieldToOrigin>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'populateProvedUnshieldToOrigin', args })
  }
  const { populateProvedUnshieldToOrigin } = await loadRailgunSdk()
  return populateProvedUnshieldToOrigin(...args)
}

// Recipients (token + amount + ORIGIN public address) the SDK derives from the
// blocked shield tx. The same arrays must be threaded through estimate → proof →
// populate unchanged, so they ride along in the prepared gas details.
export type UnshieldToOriginRecipients = {
  erc20AmountRecipients: RailgunERC20AmountRecipient[]
  nftAmountRecipients: RailgunNFTAmountRecipient[]
}

export type PreparedUnshieldToOriginGasDetails = {
  transactionGasDetails: TransactionGasDetails
  erc20AmountRecipients: RailgunERC20AmountRecipient[]
  nftAmountRecipients: RailgunNFTAmountRecipient[]
}

function requireNetworkAndRpc(chainId: number): {
  networkName: ReturnType<typeof getNetworkNameForChainId>
  rpcUrl: string
} {
  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield to origin: ${chainId}`)
  }
  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }
  return { networkName, rpcUrl }
}

/**
 * Ask the SDK who the origin recipient is for a blocked shield. No secret needed
 * — it reads the wallet's own history for `originalShieldTxid`. The returned
 * `erc20AmountRecipients[0]` carries the token, amount, and origin public address
 * the UI shows in the confirm screen.
 */
export async function getUnshieldToOriginRecipientsWithSDK(params: {
  chainId: number
  railgunWalletID: string
  originalShieldTxid: string
}): Promise<UnshieldToOriginRecipients> {
  const { chainId, railgunWalletID, originalShieldTxid } = params
  const { networkName } = requireNetworkAndRpc(chainId)

  await warmMainEngineForChain(chainId, railgunWalletID)

  const { erc20AmountRecipients, nftAmountRecipients } =
    await invokeGetRecipientsForUnshieldToOrigin([
      TXIDVersion.V2_PoseidonMerkle,
      networkName!,
      railgunWalletID,
      originalShieldTxid
    ])

  if (!erc20AmountRecipients.length && !nftAmountRecipients.length) {
    throw new Error('No recoverable balance found for this shield transaction.')
  }
  return { erc20AmountRecipients, nftAmountRecipients }
}

export async function estimateUnshieldToOriginGas(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  railgunWalletID: string
  encryptionKey: string
  originalShieldTxid: string
  recipients: UnshieldToOriginRecipients
}): Promise<UnshieldGasEstimation> {
  const { mnemonic, chainId, railgunWalletID, encryptionKey, originalShieldTxid, recipients } =
    params
  const { networkName, rpcUrl } = requireNetworkAndRpc(chainId)

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  await warmMainEngineForChain(chainId, railgunWalletID)

  const { gasEstimate } = await invokeGasEstimateForUnshieldToOrigin([
    originalShieldTxid,
    TXIDVersion.V2_PoseidonMerkle,
    networkName!,
    railgunWalletID,
    encryptionKey,
    recipients.erc20AmountRecipients,
    recipients.nftAmountRecipients
  ])

  // sendWithPublicWallet is implicitly true (no broadcaster) — pass it to the
  // shared gas helper so it prices a self-paid public tx.
  const gasDetails = await getGasDetailsForTransaction(
    networkName!,
    gasEstimate,
    true,
    wallet,
    chainId
  )
  const feeSummary = await computeGasFeeNativeAndUsd({ gasEstimate, gasDetails, chainId })
  const feeOptions = await computeGasFeeOptionsNativeAndUsd({ gasEstimate, chainId, wallet })
  return { ...feeSummary, feeOptions }
}

export async function generateUnshieldToOriginProofWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  railgunWalletID: string
  encryptionKey: string
  originalShieldTxid: string
  recipients: UnshieldToOriginRecipients
  feeSpeed?: RailgunGasSpeedName
}): Promise<PreparedUnshieldToOriginGasDetails> {
  const {
    mnemonic,
    chainId,
    railgunWalletID,
    encryptionKey,
    originalShieldTxid,
    recipients,
    feeSpeed
  } = params
  const { networkName, rpcUrl } = requireNetworkAndRpc(chainId)

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  await warmMainEngineForChain(chainId, railgunWalletID)

  const { gasEstimate } = await invokeGasEstimateForUnshieldToOrigin([
    originalShieldTxid,
    TXIDVersion.V2_PoseidonMerkle,
    networkName!,
    railgunWalletID,
    encryptionKey,
    recipients.erc20AmountRecipients,
    recipients.nftAmountRecipients
  ])

  const transactionGasDetails = await getGasDetailsForTransaction(
    networkName!,
    gasEstimate,
    true,
    wallet,
    chainId,
    feeSpeed
  )

  const progressCallback = (progress: number) => {
    if (RAILGUN_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[Railgun unshield-to-origin] proof progress', progress)
    }
  }

  try {
    await invokeGenerateUnshieldToOriginProof(
      [
        originalShieldTxid,
        TXIDVersion.V2_PoseidonMerkle,
        networkName!,
        railgunWalletID,
        encryptionKey,
        recipients.erc20AmountRecipients,
        recipients.nftAmountRecipients,
        progressCallback
      ],
      progressCallback
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('generateUnshieldToOriginProof failed', err)
    wrapUnshieldToOriginError(err)
  }

  return {
    transactionGasDetails,
    erc20AmountRecipients: recipients.erc20AmountRecipients,
    nftAmountRecipients: recipients.nftAmountRecipients
  }
}

export async function executePreparedUnshieldToOriginWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  railgunWalletID: string
  gasDetails: PreparedUnshieldToOriginGasDetails
}): Promise<string> {
  const { mnemonic, chainId, railgunWalletID, gasDetails } = params
  const { networkName, rpcUrl } = requireNetworkAndRpc(chainId)

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  try {
    const { transaction } = await invokePopulateProvedUnshieldToOrigin([
      TXIDVersion.V2_PoseidonMerkle,
      networkName!,
      railgunWalletID,
      gasDetails.erc20AmountRecipients,
      gasDetails.nftAmountRecipients,
      gasDetails.transactionGasDetails
    ])

    // Simulate before broadcasting to catch an invalid proof early.
    try {
      await provider.call({
        to: transaction.to,
        data: transaction.data,
        from: wallet.address,
        value: transaction.value ?? 0n
      })
    } catch (simErr) {
      // eslint-disable-next-line no-console
      console.error('[Railgun unshield-to-origin] simulation failed:', simErr)
      throw new Error(
        `Unshield-to-origin transaction would revert on-chain. This usually means the ZK proof is ` +
          `invalid (stale Merkle tree or corrupted circuit artifacts). Please try again. ` +
          `Details: ${simErr instanceof Error ? simErr.message : String(simErr)}`
      )
    }

    const tx = await wallet.sendTransaction(transaction)
    await tx.wait()
    return tx.hash
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('populateProvedUnshieldToOrigin / broadcast failed', err)
    wrapUnshieldToOriginError(err)
  }
}
